import { Manifest, ProductId, ActivityConfig, BaseMapStyleConfig, LayerItemConfig, CustomActivityConfig, LayerCategoryInfo } from './types';
import defaultActivitiesJson from './config/activities.generated.json';
import * as pmtiles from 'pmtiles';
import { getActiveSessionToken } from './state';



export const DEFAULT_MANIFEST_PATH = 'manifest.json';

export function getProductSubdir(manifest: Manifest | null, product: string, model?: string): string {
  if (model === 'gw_nowcast' && ['goldenwindow', 'sunrise', 'sunset', 'goldenhour', 'golden_hour', 'alpenglow', 'alpenglow_forecast'].includes(product)) {
    return 'observed/goldenwindow';
  }
  if (model === 'rtma' && (product === 'hrtemp' || product === 'temperature' || product === 'mountain_temps' || product === 'surface_winds' || product === 'winds' || product === 'wind')) {
    return 'observed/rtma';
  }
  if (model === 'mrms' && product === 'live_weather_qpe') {
    return 'observed/mrms';
  }
  if (product === 'precip_accum' || product === 'rain_accum') {
    return 'observed/precip_accum';
  }

  if (manifest?.products && manifest.products[product]) {
    const p = manifest.products[product];
    return `${p.category}/${p.dir}`;
  }

  switch (product) {
    // Observed products
    case 'satellite':
    case 'satellite_trans':
    case 'regional_high_clouds':
    case 'regional_mid_clouds':
    case 'regional_fog_sea':
    case 'regional_base_sat':
    case 'goes_composite':
    case 'goes_west':
    case 'goes_east':
    case 'himawari':
    case 'meteosat':
    case 'global_ir':
    case 'gmgsi':
      return 'observed/satellite';
    case 'mrms':
    case 'mrms_radar':
    case 'mrms_qpe':
    case 'mrms_snow':
    case 'mrms_lightning':
      return 'observed/mrms';
    case 'rtma':
    case 'rtma_surface_wind':
      return 'observed/rtma';
    case 'streamflow':
    case 'streamflow_gauges':
    case 'usgs_streamflow':
      return 'observed/streamflow';
    case 'aqi_stations':
      return 'observed/aqi_stations';
    case 'wx_stations':
      return 'observed/wx_stations';
    case 'sst':
    case 'ndbc_buoys':
      return 'observed/sst';
    case 'wildfires':
    case 'wildfire_perimeters':
      return 'observed/wildfires';
    case 'fire_restrictions':
      return 'observed/fire_restrictions';
    case 'avalanche':
    case 'avalanche_danger':
      return 'observed/avalanche';
    case 'snow_depth':
    case 'snow_stations':
      return 'observed/snow_depth';
    case 'snow_accum':
      return 'observed/snow_accum';

    // Forecast products
    case 'goldenwindow':
    case 'sunrise':
    case 'sunset':
    case 'goldenhour':
    case 'golden_hour':
    case 'alpenglow':
    case 'alpenglow_forecast':
      return 'forecast/goldenwindow';
    case 'darkskies':
    case 'dark_skies':
      return 'forecast/darkskies';
    case 'surface_winds':
    case 'winds':
    case 'wind':
      if (model === 'rtma') return 'observed/rtma';
      return 'forecast/surface_winds';
    case 'lenticular':
      return 'forecast/lenticular';
    case 'clouds':
    case 'cloud':
      return 'forecast/clouds';
    case 'hrtemp':
    case 'hrtemp_contours':
    case 'hrtemp_current':
    case 'temperature':
    case 'mountain_temps':
      if (model === 'rtma' || product === 'hrtemp_current') return 'observed/rtma';
      return 'forecast/hrtemp';

    case 'lightning':
    case 'lightning_24h':
      return 'forecast/lightning';
    case 'aurora':
      return 'observed/aurora';
    case 'smoke':
    case 'dust':
    case 'dust_forecast':
    case 'aqi':
    case 'aqhi':
      return 'forecast/smoke';
    case 'precip':
    case 'precipitation':
    case 'precip_prob':
    case 'precip_24h':
    case 'snow_24h':
      return 'forecast/precip';
    case 'uv':
      return 'forecast/uv';
    case 'sun_intensity':
      return 'forecast/sun_intensity';
    case 'valley_fog':
      return 'forecast/valley_fog';
    case 'cloud_inversion':
      return 'forecast/cloud_inversion';
    case 'cloud_base':
      return 'forecast/cloud_base';
    case 'mountain_visibility':
      return 'forecast/mountain_visibility';
    case 'windchill':
      return 'forecast/windchill';
    case 'relative_humidity':
      return 'forecast/relative_humidity';
    case 'dewpoint':
      return 'forecast/dewpoint';
    case 'cape':
      return 'forecast/cape';
    case 'thunderstorm':
      return 'forecast/thunderstorm';
    case 'freezing_level':
    case 'freezing_level_contours':
      return 'forecast/freezing_level';
    case 'optics_dew_risk':
      return 'forecast/optics_dew_risk';
    case 'seeing':
      return 'forecast/seeing';
    case 'turbulence':
    case 'aviation_turbulence':
      return 'forecast/turbulence';
    case 'jet_stream':
      return 'forecast/jet_stream';
    case 'foliage':
    case 'foliage_treed':
    case 'foliage_vibrancy':
    case 'foliage_vibrancy_treed':
    case 'foliage_peak_date':
    case 'foliage_peak_date_treed':
    case 'foliage_canopy':
    case 'foliage_species':
    case 'foliage_species_maple':
    case 'foliage_species_aspen':
    case 'foliage_species_oak':
    case 'foliage_species_larch':
    case 'foliage_summer_temp_anomaly':
    case 'foliage_soil_moisture_anomaly':
      return 'forecast/foliage';
    case 'mushrooms':
    case 'mushrooms_current':
    case 'mushrooms_outlook':
      return 'observed/mushrooms';
    case 'alpine_wildflowers':
      return 'forecast/alpine_wildflowers';
    case 'desert_wildflowers':
      return 'forecast/desert_wildflowers';
    case 'wild_berries':
      return 'forecast/wild_berries';

    // Climatology & Basemaps
    case 'climate':
    case 'climate_daily':
    case 'climate_monthly':
    case 'climate_weekly':
      return 'climate_tiles';
    case 'recent_climate':
      return 'observed/climate_observed';
    case 'basemaps':
      return 'basemaps';

    default:
      return '';
  }
}

export function getApiServer(manifest?: Manifest | null): string {
  let apiServer = manifest ? manifest.api_server : '';
  if (!apiServer && typeof window !== 'undefined') {
    const tileBase = manifest?.tile_base || 'tiles';
    if (tileBase.startsWith('/public_tiles') || tileBase.startsWith('public_tiles')) {
      apiServer = window.location.origin;
    } else {
      const pathname = window.location.pathname;
      const subfolder = pathname.substring(0, pathname.lastIndexOf('/') + 1);
      apiServer = window.location.origin + subfolder;
    }
  }
  return apiServer ? (apiServer.endsWith('/') ? apiServer.slice(0, -1) : apiServer) : '';
}

export function getForecastSliceInfo(manifest: Manifest, product: ProductId, model: string, date: string, hour: string = ''): { prefix: string; ext: string; resolvedTileBase: string } {
  // MapLibre compatible template URL builder
  const tileBase = manifest.tile_base || 'tiles';
  const tileRoot = tileBase.replace(/^\/+|\/+$/g, '');
  const subDir = getProductSubdir(manifest, product, model);
  const resolvedTileBase = subDir ? `${tileRoot}/${subDir}` : tileRoot;

  let prefix = '';
  let ext = 'webp';

  switch (product) {
    case 'sunrise':
    case 'alpenglow':
    case 'goldenhour': {
      const defaultModel = 'gw_v2_blend_50';
      const effectiveModel = model || defaultModel;
      prefix = `v5_${effectiveModel}_${date}_${hour}`;
      break;
    }
    case 'darkskies':
      const dsModel = (model === 'bortle' || !model) ? 'blend' : model;
      prefix = `darkskies_${dsModel}_${date}_Z${hour}`;
      break;
    case 'lenticular':
      // e.g. lenticular_ecmwf_cap_2026-07-06_Z12
      const effectiveLentModel = (model === 'blend' || model === 'ensemble' || !model) ? 'ecmwf' : model;
      prefix = `lenticular_${effectiveLentModel}_${hour}`; // hour acts as mode_date_Zhour here
      ext = 'pmtiles';
      break;
    case 'clouds':
      // e.g. cloud_ecmwf_low_2026-07-06_00UTC
      prefix = `cloud_${model}_${hour}`; // hour acts as level_date_ZhourUTC here
      break;
    case 'hrtemp':
    case 'hrtemp_current': {
      let hrtempModel = model;
      if (product === 'hrtemp_current' || model === 'rtma') {
        hrtempModel = 'rtma';
      } else if (model === 'blend' || !model) {
        hrtempModel = 'nbm';
      } else if (manifest?.models_config?.[model]?.name_on_disk) {
        hrtempModel = manifest.models_config[model].name_on_disk;
      } else if (model === 'nbm_highres') {
        hrtempModel = 'skysk_hres_topo';
      }
      prefix = `hrtemp_${hrtempModel}_${date}_Z${hour}`;
      break;
    }
    case 'hrtemp_contours': {
      let hrtempModel = model;
      if (model === 'blend' || !model) {
        hrtempModel = 'skysk_hres_topo';
      } else if (manifest?.models_config?.[model]?.name_on_disk) {
        hrtempModel = manifest.models_config[model].name_on_disk;
      } else if (model === 'nbm_highres') {
        hrtempModel = 'skysk_hres_topo';
      }
      prefix = `hrtemp_${hrtempModel}_${date}_Z${hour}_contours`;
      ext = 'pmtiles';
      break;
    }

    case 'lightning':
      if (hour) {
        prefix = (model === 'blend' || !model) ? `lightning_${date}_Z${hour}` : `lightning_${model}_${date}_Z${hour}`;
      } else {
        prefix = (model === 'blend' || !model) ? `lightning_24h_${date}` : `lightning_24h_${model}_${date}`;
      }
      break;
    case 'lightning_24h':
      prefix = (model === 'blend' || !model) ? `lightning_24h_${date}` : `lightning_24h_${model}_${date}`;
      break;
    case 'winds':
      prefix = `surface_wind_${model}_${date}_Z${hour}`;
      break;
    case 'smoke':
      // hour format acts as: level_date_Zhour (e.g. surface_2026-07-09_Z03)
      prefix = `smoke_${model}_${hour}`;
      break;
    case 'dust':
    case 'dust_forecast':
      prefix = `smoke_gefs_dust_surface_${date}_Z${hour}`;
      break;
    case 'aqi':
      // hour format acts as: aqiMode_model_date_Zhour (e.g. aqi_raqdps_2026-07-09_Z03)
      prefix = `${hour}`;
      break;
    case 'precip':
      prefix = `precip_${model}_${date}_Z${hour}`;
      break;
    case 'precip_prob':
      prefix = `precip_prob_${model}_${date}_Z${hour}`;
      break;
    case 'precip_24h':
      prefix = `precip_24h_${model}_${date}`;
      break;
    case 'snow_24h':
      prefix = `snow_24h_${model}_${date}`;
      break;
    case 'sun_intensity':
      const siModel = (model === 'blend' || !model) ? 'rrfs' : model;
      prefix = hour ? `sun_intensity_${siModel}_${date}_Z${hour}` : `sun_intensity_${siModel}_${date}`;
      ext = 'pmtiles';
      break;
    case 'freezing_level':
      const flModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `freezing_level_${flModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'windchill':
      const wcModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `windchill_${wcModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'wildfires':
      prefix = `wildfires`;
      break;
    case 'fire_restrictions':
      prefix = `fire_restrictions`;
      break;
    case 'avalanche':
      prefix = `avalanche`;
      break;
    case 'aurora':
      prefix = `aurora_${date}`;
      ext = 'png';
      break;
    case 'optics_dew_risk':
      const odModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `gw_optics_dew_risk_${odModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'seeing':
      const seeingModel = (model === 'blend' || !model) ? 'rdps' : model;
      prefix = `gw_seeing_${seeingModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'turbulence':
    case 'aviation_turbulence':
      const turbModel = (model === 'blend' || !model) ? 'gfs' : model;
      prefix = `turbulence_${turbModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'jet_stream':
      const jsModel = (model === 'blend' || !model) ? 'gfs' : model;
      prefix = `jet_stream_${jsModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'valley_fog':
      const fogModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `gw_valley_fog_${fogModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'cloud_inversion':
      const ciModel = (model === 'blend' || !model) ? 'nbm' : model;
      prefix = `gw_cloud_inversion_${ciModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'cloud_base':
      const cbModel = (model === 'blend' || !model) ? 'nbm' : model;
      prefix = `gw_cloud_base_${cbModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'mountain_visibility':
      const mvModel = (model === 'blend' || !model) ? 'nbm' : model;
      prefix = `gw_mountain_visibility_${mvModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'relative_humidity':
      const rhModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `relative_humidity_${rhModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'dewpoint':
      const dpModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `dewpoint_${dpModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'cape':
      const capeModel = (model === 'blend' || !model) ? 'blend' : model;
      prefix = `cape_${capeModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'thunderstorm':
      const tsModel = (model === 'blend' || !model) ? 'hrrr' : model;
      prefix = `thunderstorm_${tsModel}_${date}_Z${hour}`;
      ext = 'pmtiles';
      break;
    case 'mushrooms':
    case 'mushrooms_current':
      prefix = (model && model !== 'flush' && model !== 'general_flush') ? `mushrooms_current_${model}` : 'mushrooms_current';
      ext = 'pmtiles';
      break;
    case 'mushrooms_outlook':
      prefix = (model && model !== 'flush' && model !== 'general_flush') ? `mushrooms_outlook_${model}` : 'mushrooms_outlook';
      ext = 'pmtiles';
      break;
    case 'foliage':
    case 'foliage_treed':
      prefix = product === 'foliage_treed' ? `foliage_treed_${date}` : `foliage_${date}`;
      ext = 'pmtiles';
      break;
    case 'foliage_vibrancy':
    case 'foliage_vibrancy_treed':
      prefix = product === 'foliage_vibrancy_treed' ? 'foliage_vibrancy_treed' : 'foliage_vibrancy';
      ext = 'pmtiles';
      break;
    case 'foliage_peak_date':
      prefix = 'foliage_peak_date';
      ext = 'pmtiles';
      break;
    case 'foliage_peak_date_treed':
      prefix = 'foliage_peak_date_treed';
      ext = 'pmtiles';
      break;
    case 'foliage_canopy':
      prefix = 'foliage_canopy';
      ext = 'pmtiles';
      break;
    case 'foliage_species':
      prefix = 'foliage_species';
      ext = 'pmtiles';
      break;
    case 'foliage_species_maple':
      prefix = 'foliage_species_maple';
      ext = 'pmtiles';
      break;
    case 'foliage_species_aspen':
      prefix = 'foliage_species_aspen';
      ext = 'pmtiles';
      break;
    case 'foliage_species_oak':
      prefix = 'foliage_species_oak';
      ext = 'pmtiles';
      break;
    case 'foliage_species_larch':
      prefix = 'foliage_species_larch';
      ext = 'pmtiles';
      break;
    case 'foliage_summer_temp_anomaly':
      prefix = 'foliage_summer_temp_anomaly';
      ext = 'pmtiles';
      break;
    case 'foliage_soil_moisture_anomaly':
      prefix = 'foliage_soil_moisture_anomaly';
      ext = 'pmtiles';
      break;
    case 'alpine_wildflowers': {
      const mode = model || 'peak_date';
      const domain = date || 'na';
      const target = hour || 'composite';
      if (mode === 'status') {
        prefix = `alpine_wildflowers_status_${domain}_${target}`;
      } else if (mode === 'mbii') {
        prefix = `alpine_wildflowers_mbii_${domain}_${target}`;
      } else {
        prefix = `alpine_wildflowers_peak_date_${domain}_${target}`;
      }
      ext = 'pmtiles';
      break;
    }
    case 'desert_wildflowers': {
      const mode = model || 'stage';
      const domain = date || 'na';
      const target = hour || '';
      if (mode === 'bii') {
        prefix = `desert_wildflowers_bii_${domain}_${target}`;
      } else if (mode === 'peak_date') {
        prefix = `desert_wildflowers_peak_date_${domain}`;
      } else {
        prefix = `desert_wildflowers_stage_${domain}_${target}`;
      }
      ext = 'pmtiles';
      break;
    }
    case 'wild_berries': {
      const mode = model || 'stage';
      const domain = date || 'na';
      const target = hour || 'huckleberry';
      if (mode === 'quality') {
        prefix = `wild_berries_quality_${domain}_${target}`;
      } else if (mode === 'peak_date') {
        prefix = `wild_berries_peak_date_${domain}_${target}`;
      } else {
        prefix = `wild_berries_stage_${domain}_${target}`;
      }
      ext = 'pmtiles';
      break;
    }
    case 'snow_depth': {
      const prodPrefix = 'snow_depth';
      const mKey = (model && model !== 'snodas' && model !== 'default') ? `_${model}` : '';
      prefix = date ? `${prodPrefix}${mKey}_${date}` : `${prodPrefix}${mKey}`;
      ext = 'pmtiles';
      break;
    }
    case 'snow_accum': {
      const mKey = (model && model !== 'default') ? model : 'ecmwf';
      const intervalKey = hour || '24h';
      prefix = date ? `snow_accum_${mKey}_${intervalKey}_${date}` : `snow_accum_${mKey}_${intervalKey}`;
      ext = 'pmtiles';
      break;
    }
    case 'precip_accum':
    case 'rain_accum': {
      if (model === 'mrms' && hour === '1h') {
        const mrmsEntry = (manifest as any)?.live_weather?.['mrms_qpe_1h'];
        const latestScan = mrmsEntry?.latest_scan || mrmsEntry?.scans?.[mrmsEntry?.scans?.length - 1];
        prefix = latestScan ? `mrms_qpe_1h_${latestScan}` : `mrms_qpe_1h`;
      } else {
        const mKey = (model && model !== 'default') ? model : 'cmorph2';
        const intervalKey = hour || '24h';
        prefix = date ? `precip_accum_${mKey}_${intervalKey}_${date}` : `precip_accum_${mKey}_${intervalKey}`;
      }
      ext = 'pmtiles';
      break;
    }
    case 'climate':
    case 'climate_daily':
    case 'climate_monthly':
    case 'climate_weekly': {
      let cMode = 'daily';
      if (hour && (hour === 'daily' || hour === 'monthly' || hour === 'weekly')) {
        cMode = hour;
      } else if (product.includes('_')) {
        cMode = product.split('_')[1];
      }

      let cVar = model || 'tmax_mean';
      let targetDoy = 196;
      let targetMonth = 7;
      let targetWeek = 5;

      if (date && date.includes('-')) {
        const parts = date.split('-').map(Number);
        const y = parts[0] || 2026;
        const m = parts[1] || 8;
        const d = parts[2] || 15;
        const dObj = new Date(Date.UTC(y, m - 1, d));
        const start = new Date(Date.UTC(y, 0, 0));
        targetDoy = Math.max(1, Math.min(365, Math.floor((dObj.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))));
        targetMonth = m;
        targetWeek = Math.max(1, Math.min(52, Math.ceil(targetDoy / 7)));
      } else if (date && date.startsWith('m')) {
        targetMonth = parseInt(date.substring(1), 10) || 7;
      } else if (date && date.startsWith('w')) {
        targetWeek = parseInt(date.substring(1), 10) || 5;
      } else if (date && date.startsWith('doy_')) {
        targetDoy = parseInt(date.substring(4), 10) || 196;
      }

      let cTime = '';
      if (cMode === 'daily') {
        cTime = `doy_${String(targetDoy).padStart(3, '0')}`;
      } else if (cMode === 'monthly') {
        cTime = `m${String(targetMonth).padStart(2, '0')}`;
      } else {
        cTime = `w${String(targetWeek).padStart(2, '0')}`;
      }

      // Check available timesteps in manifest.climate.available
      const availObj = manifest?.climate?.available;
      if (availObj && availObj[cMode]) {
        let availList = availObj[cMode][cVar];

        // Handle variable aliases (precip_monthly <-> precip, snow_monthly <-> snow, wind_speed <-> wind, mostly_sunny_monthly <-> mostly_sunny, etc.)
        if (!availList || availList.length === 0) {
          if (cVar.endsWith('_monthly') && availObj[cMode][cVar.replace('_monthly', '')]) {
            cVar = cVar.replace('_monthly', '');
            availList = availObj[cMode][cVar];
          } else if (availObj[cMode][`${cVar}_monthly`]) {
            cVar = `${cVar}_monthly`;
            availList = availObj[cMode][cVar];
          } else if (cVar === 'precip_monthly' && availObj[cMode]['precip']) {
            cVar = 'precip';
            availList = availObj[cMode]['precip'];
          } else if (cVar === 'precip' && availObj[cMode]['precip_monthly']) {
            cVar = 'precip_monthly';
            availList = availObj[cMode]['precip_monthly'];
          } else if (cVar === 'snow_monthly' && availObj[cMode]['snow']) {
            cVar = 'snow';
            availList = availObj[cMode]['snow'];
          } else if (cVar === 'snow' && availObj[cMode]['snow_monthly']) {
            cVar = 'snow_monthly';
            availList = availObj[cMode]['snow_monthly'];
          } else if (cVar === 'wind_speed' && availObj[cMode]['wind']) {
            cVar = 'wind';
            availList = availObj[cMode]['wind'];
          } else if (cVar === 'wind' && availObj[cMode]['wind_speed']) {
            cVar = 'wind_speed';
            availList = availObj[cMode]['wind_speed'];
          }
        }

        // If exact variable still isn't in manifest, fallback to first available variable in that mode
        if (!availList || availList.length === 0) {
          const firstVar = Object.keys(availObj[cMode])[0];
          if (firstVar) {
            cVar = firstVar;
            availList = availObj[cMode][firstVar];
          }
        }

        if (availList && availList.length > 0) {
          // Find closest timestep in available list
          if (cMode === 'daily') {
            const requestedNum = targetDoy;
            let closest = availList[0];
            let minDiff = 9999;
            for (const step of availList) {
              const diff = Math.abs(parseInt(step, 10) - requestedNum);
              if (diff < minDiff) {
                minDiff = diff;
                closest = step;
              }
            }
            cTime = `doy_${closest.padStart(3, '0')}`;
          } else if (cMode === 'monthly') {
            const requestedNum = targetMonth;
            let closest = availList[0];
            let minDiff = 9999;
            for (const step of availList) {
              const diff = Math.abs(parseInt(step, 10) - requestedNum);
              if (diff < minDiff) {
                minDiff = diff;
                closest = step;
              }
            }
            cTime = `m${closest.padStart(2, '0')}`;
          } else if (cMode === 'weekly') {
            const requestedNum = targetWeek;
            let closest = availList[0];
            let minDiff = 9999;
            for (const step of availList) {
              const diff = Math.abs(parseInt(step, 10) - requestedNum);
              if (diff < minDiff) {
                minDiff = diff;
                closest = step;
              }
            }
            cTime = `w${closest.padStart(2, '0')}`;
          }
        }
      }

      if (cMode === 'daily') {
        prefix = `climate_daily_${cVar}_${cTime}`;
      } else {
        prefix = `climate_${cVar}_${cTime}`;
      }
      ext = 'pmtiles';
      break;
    }
    case 'recent_climate': {
      const rcVar = model || 'tanom';
      const rcPeriod = date || 'ytd';
      const rcYear = hour || String(manifest?.recent_climate?.latest_year || new Date().getFullYear());
      prefix = `climate_${rcVar}_${rcPeriod}_${rcYear}`;
      ext = 'pmtiles';
      break;
    }
  }
  return { prefix, ext, resolvedTileBase };
}

export const GLOBAL_COORDINATES: [[number, number], [number, number], [number, number], [number, number]] = [
  [-180.0, 85.051129],
  [180.0, 85.051129],
  [180.0, -85.051129],
  [-180.0, -85.051129]
];

export const STANDARD_NA_COORDINATES: [[number, number], [number, number], [number, number], [number, number]] = [
  [-170.0, 75.0],
  [-30.0, 75.0],
  [-30.0, 0.0],
  [-170.0, 0.0]
];

export function isGlobalModel(model: string): boolean {
  if (!model) return false;
  const m = model.toLowerCase();
  return [
    'ecmwf', 'gfs', 'gdps', 'icon13', 'icon', 'hgefs', 'global',
    'gefs', 'gefs_dust',
    'gw_v2_gfs', 'gw_v2_ecmwf', 'gw_v2_gdps', 'gw_v2_icon13',
    'gw_v1_gfs', 'gw_v1_ecmwf'
  ].includes(m);
}

export function getModelCoordinates(
  manifest: Manifest | null | undefined,
  _product: ProductId,
  model: string
): [[number, number], [number, number], [number, number], [number, number]] {
  const onDiskModel = manifest?.models_config?.[model]?.name_on_disk || model;
  if (isGlobalModel(model) || isGlobalModel(onDiskModel)) {
    return GLOBAL_COORDINATES;
  }
  return STANDARD_NA_COORDINATES;
}

const singleImageUnavailableKeys = new Set<string>();

export function markSingleImageUnavailable(product: string, model?: string): void {
  if (model) {
    singleImageUnavailableKeys.add(`${product}:${model}`);
  }
  singleImageUnavailableKeys.add(product);
}

export function isSingleImageMarkedUnavailable(product: string, model?: string): boolean {
  if (model && singleImageUnavailableKeys.has(`${product}:${model}`)) {
    return true;
  }
  return singleImageUnavailableKeys.has(product);
}

const SINGLE_IMAGE_PARENT_PRODUCT_MAP: Record<string, string> = {
  sunrise: 'goldenwindow',
  sunset: 'goldenwindow',
  alpenglow: 'goldenwindow',
  goldenhour: 'goldenwindow',
  precip_prob: 'precip',
  precip_24h: 'precip',
  snow_24h: 'precip',
  dust: 'smoke',
  dust_forecast: 'smoke',
  aqi: 'smoke',
  aqhi: 'smoke',
  aviation_turbulence: 'turbulence',
};

const DEFAULT_SINGLE_IMAGE_PRODUCTS = [
  'clouds', 'goldenwindow', 'sunrise', 'sunset', 'alpenglow', 'goldenhour',
  'valley_fog', 'darkskies', 'lenticular', 'optics_dew_risk', 'seeing',
  'turbulence', 'aviation_turbulence', 'jet_stream', 'sun_intensity',
  'hrtemp', 'precip', 'precip_prob', 'precip_24h', 'snow_24h',
  'freezing_level', 'relative_humidity', 'dewpoint', 'windchill'
];

export function isSingleImageProduct(manifest: Manifest | null | undefined, product: ProductId, model?: string): boolean {
  if (!manifest) return false;
  if (product === 'hrtemp_current' || model === 'rtma') return false;
  if (isSingleImageMarkedUnavailable(product, model)) return false;

  const manifestList: string[] = (manifest as any).single_image_products || [];
  const combined = new Set<string>([...DEFAULT_SINGLE_IMAGE_PRODUCTS, ...manifestList]);
  if (combined.has(product)) return true;

  const parent = SINGLE_IMAGE_PARENT_PRODUCT_MAP[product];
  if (parent && combined.has(parent)) return true;
  return false;
}

export function getForecastCycleVersion(manifest: Manifest | null | undefined): string {
  const mvUtc = (manifest as any)?.model_versions?.generated_at_utc;
  if (typeof mvUtc === 'string' && mvUtc.length > 0) {
    return mvUtc;
  }
  const cycleEpoch = manifest?.forecast_point_tiles?.cycle_epoch;
  if (cycleEpoch) {
    return String(cycleEpoch);
  }
  return '';
}

export function getForecastSingleImageUrl(
  manifest: Manifest,
  product: ProductId,
  model: string,
  date: string,
  hour: string = '',
  resolution: 1024 | 2048 | 4096 = 2048
): string {
  const { prefix, resolvedTileBase } = getForecastSliceInfo(manifest, product, model, date, hour);
  const apiServer = getApiServer(manifest);
  const serverPath = apiServer ? (apiServer.endsWith('/') ? apiServer.slice(0, -1) : apiServer) : '';
  const basePath = resolvedTileBase.startsWith('/') ? resolvedTileBase : `/${resolvedTileBase}`;
  const ver = model === 'gw_nowcast'
    ? ((manifest as any)?.generated_at || getForecastCycleVersion(manifest))
    : getForecastCycleVersion(manifest);
  const verSuffix = ver ? `?v=${encodeURIComponent(ver)}` : '';
  return appendAuthToken(cleanUrl(`${serverPath}${basePath}/${prefix}_${resolution}.webp${verSuffix}`));
}

export function getForecastBoundsUrl(
  manifest: Manifest,
  product: ProductId,
  model: string,
  date: string,
  hour: string = ''
): string {
  const { prefix, resolvedTileBase } = getForecastSliceInfo(manifest, product, model, date, hour);
  const apiServer = getApiServer(manifest);
  const serverPath = apiServer ? (apiServer.endsWith('/') ? apiServer.slice(0, -1) : apiServer) : '';
  const basePath = resolvedTileBase.startsWith('/') ? resolvedTileBase : `/${resolvedTileBase}`;
  const ver = model === 'gw_nowcast'
    ? ((manifest as any)?.generated_at || getForecastCycleVersion(manifest))
    : getForecastCycleVersion(manifest);
  const verSuffix = ver ? `?v=${encodeURIComponent(ver)}` : '';
  return appendAuthToken(cleanUrl(`${serverPath}${basePath}/${prefix}_bounds.json${verSuffix}`));
}

export function getTileUrl(
  manifest: Manifest,
  product: ProductId,
  model: string,
  date: string,
  hour: string = '',
  forcePmtiles: boolean = false
): string {
  if (!forcePmtiles && isSingleImageProduct(manifest, product, model)) {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const is4K = typeof window !== 'undefined' && window.innerWidth >= 2560;
    // 4096 resolution is built on disk for hrtemp, freezing_level, valley_fog, and goldenwindow
    const supports4K = ['hrtemp', 'freezing_level', 'valley_fog', 'goldenwindow', 'sunrise', 'sunset', 'alpenglow', 'goldenhour'].includes(product);
    const res: 1024 | 2048 | 4096 = isMobile ? 1024 : (is4K && supports4K ? 4096 : 2048);
    return getForecastSingleImageUrl(manifest, product, model, date, hour, res);
  }

  const { prefix, ext, resolvedTileBase } = getForecastSliceInfo(manifest, product, model, date, hour);
  const tileBase = manifest.tile_base || 'tiles';
  const apiServer = getApiServer(manifest);

  // Non-PMTiles layers: winds, lightning only hold JSON wind grids/scores.
  // We return a standard URL for them.
  const isPmtilesProduct = ['sunrise', 'alpenglow', 'goldenhour', 'darkskies', 'lenticular', 'clouds', 'hrtemp', 'hrtemp_current', 'hrtemp_contours', 'smoke', 'aqi', 'precip', 'precip_prob', 'precip_24h', 'snow_24h', 'snow_depth', 'snow_accum', 'precip_accum', 'sun_intensity', 'aurora', 'freezing_level', 'windchill', 'optics_dew_risk', 'seeing', 'valley_fog', 'cloud_inversion', 'cloud_base', 'mountain_visibility', 'relative_humidity', 'dewpoint', 'cape', 'thunderstorm', 'foliage', 'foliage_treed', 'foliage_vibrancy', 'foliage_vibrancy_treed', 'foliage_peak_date', 'foliage_peak_date_treed', 'foliage_canopy', 'foliage_species', 'foliage_species_maple', 'foliage_species_aspen', 'foliage_species_oak', 'foliage_species_larch', 'foliage_summer_temp_anomaly', 'foliage_soil_moisture_anomaly', 'climate', 'climate_daily', 'climate_monthly', 'climate_weekly', 'recent_climate', 'mushrooms', 'mushrooms_current', 'mushrooms_outlook', 'alpine_wildflowers', 'desert_wildflowers', 'wild_berries', 'turbulence', 'aviation_turbulence', 'jet_stream'].includes(product);
  const serverPath = apiServer ? (apiServer.endsWith('/') ? apiServer.slice(0, -1) : apiServer) : '';
  const basePath = resolvedTileBase.startsWith('/') ? resolvedTileBase : `/${resolvedTileBase}`;

  if (!isPmtilesProduct) {
    let rawUrl = `${serverPath}${basePath}/${prefix}/{z}/{x}/{y}.${ext}`;
    return cleanUrl(rawUrl);
  }

  // Construct PMTiles URL
  const isClimateProduct = ['climate', 'climate_daily', 'climate_monthly', 'climate_weekly'].includes(product);
  let baseUrl: string;
  if (isClimateProduct) {
    const climatePath = manifest?.climate?.tile_path || 'climate_tiles';
    const tileRoot = tileBase.startsWith('/') ? tileBase : `/${tileBase}`;
    baseUrl = `${serverPath}${tileRoot}/${climatePath}/${prefix}.pmtiles`;
  } else {
    baseUrl = `${serverPath}${basePath}/${prefix}.pmtiles`;
  }
  return buildPmtilesUrl(baseUrl);
}

export function cleanUrl(urlPath: string): string {
  if (!urlPath) return '';
  let str = urlPath.trim();

  let pmtilesPrefix = '';
  if (str.startsWith('pmtiles://')) {
    pmtilesPrefix = 'pmtiles://';
    str = str.substring('pmtiles://'.length);
  }

  let httpPrefix = '';
  if (str.startsWith('https://')) {
    httpPrefix = 'https://';
    str = str.substring('https://'.length);
  } else if (str.startsWith('http://')) {
    httpPrefix = 'http://';
    str = str.substring('http://'.length);
  }

  str = str.replace(/\/+/g, '/');
  return `${pmtilesPrefix}${httpPrefix}${str}`;
}

export function buildPmtilesUrl(urlPath: string): string {
  if (!urlPath) return '';
  let url = urlPath.trim();
  if (url.startsWith('pmtiles://')) {
    url = url.substring('pmtiles://'.length);
  }

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const subfolder = pathname.substring(0, pathname.lastIndexOf('/') + 1);
      if (url.startsWith('/')) {
        if (subfolder && subfolder !== '/' && !url.startsWith(subfolder) && !url.startsWith('/public_tiles/')) {
          url = `${window.location.origin}${subfolder}${url.replace(/^\/+/, '')}`;
        } else {
          url = `${window.location.origin}${url}`;
        }
      } else {
        url = `${window.location.origin}${subfolder}${url}`;
      }
    }
  }

  const cleaned = cleanUrl(url);
  return cleaned.startsWith('pmtiles://') ? cleaned : `pmtiles://${cleaned}`;
}

export function getSatelliteOverlayTileUrl(
  manifest: any,
  layerSuite: 'regional_high_clouds' | 'regional_mid_clouds' | 'regional_fog_sea' | 'satellite_trans',
  scanTs?: string | null
): string {
  if (!scanTs) return '';
  const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
  const apiServer = getApiServer(manifest);

  let targetScan = scanTs;
  const sourceScans: string[] = manifest?.live_weather?.satellite?.[layerSuite]?.scans;
  if (sourceScans && sourceScans.length > 0 && !sourceScans.includes(scanTs)) {
    let closest = sourceScans[0];
    let minDiff = Infinity;
    const tsNum = parseInt(scanTs, 10);
    for (const s of sourceScans) {
      const diff = Math.abs(parseInt(s, 10) - tsNum);
      if (diff < minDiff) {
        minDiff = diff;
        closest = s;
      }
    }
    targetScan = closest;
  }

  let rawUrl = cleanUrl(`${apiServer}/${tileRoot}/observed/satellite/${layerSuite}_${targetScan}.pmtiles`);
  if (!apiServer && rawUrl.startsWith('/')) {
    rawUrl = rawUrl.substring(1);
  }
  return appendAuthToken(buildPmtilesUrl(rawUrl));
}

export function buildSampleTileUrl(baseUrl: string, tileCoords: string): string {
  if (!baseUrl) return '';
  const cleanPath = baseUrl.split('?')[0];
  if (cleanPath.endsWith('.webp') && !cleanPath.includes('{z}') && !/\/\d+\/\d+\/\d+\.webp$/i.test(cleanPath)) {
    return baseUrl;
  }
  if (baseUrl.includes('{z}/{x}/{y}')) {
    return baseUrl.replace('{z}/{x}/{y}', tileCoords);
  }
  if (/\/\d+\/\d+\/\d+\.[a-zA-Z0-9]+$/.test(baseUrl)) {
    return baseUrl;
  }
  return `${baseUrl}/${tileCoords}.webp`;
}


export interface VolcanoConfig {

  key: string;
  name: string;
  lat: number;
  lon: number;
  elev: number;
}

export const VOLCANOES: VolcanoConfig[] = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.volcanoes) || [
  { key: 'Mount_Baker', name: 'Mount Baker', lat: 48.7767, lon: -121.8144, elev: 10781 },
  { key: 'Mount_Shuksan', name: 'Mount Shuksan', lat: 48.8310, lon: -121.6029, elev: 9131 },
  { key: 'Glacier_Peak', name: 'Glacier Peak', lat: 48.1119, lon: -121.1132, elev: 10541 },
  { key: 'Mount_Rainier', name: 'Mount Rainier', lat: 46.8523, lon: -121.7603, elev: 14411 },
  { key: 'Mount_Adams', name: 'Mount Adams', lat: 46.2024, lon: -121.4910, elev: 12276 },
  { key: 'Mount_St_Helens', name: 'Mount St. Helens', lat: 46.1914, lon: -122.1956, elev: 8363 },
  { key: 'Mount_Hood', name: 'Mount Hood', lat: 45.3736, lon: -121.6960, elev: 11240 },
  { key: 'Mount_Jefferson', name: 'Mount Jefferson', lat: 44.6743, lon: -121.7995, elev: 10497 },
  { key: 'North_Sister', name: 'North Sister', lat: 44.1662, lon: -121.7725, elev: 10085 },
  { key: 'Middle_Sister', name: 'Middle Sister', lat: 44.1495, lon: -121.7837, elev: 10047 },
  { key: 'South_Sister', name: 'South Sister', lat: 44.1034, lon: -121.7692, elev: 10358 },
  { key: 'Mount_Bachelor', name: 'Mount Bachelor', lat: 43.9792, lon: -121.6886, elev: 9068 },
  { key: 'Mount_Thielsen', name: 'Mount Thielsen', lat: 43.1516, lon: -122.0664, elev: 9182 },
  { key: 'Crater_Lake', name: 'Crater Lake', lat: 42.9446, lon: -122.1090, elev: 8159 },
  { key: 'Mount_Shasta', name: 'Mount Shasta', lat: 41.3099, lon: -122.3106, elev: 14179 }
];

// Helper to get available times for clouds
export function getAvailableCloudsTimes(manifest: Manifest, model: string): TimeStep[] {
  const range: TimeStep[] = [];
  if (!manifest?.clouds) return range;
  const modelData = manifest.clouds.available_by_model?.[model] || manifest.clouds.available?.[model] || {};
  const dateHourSet = new Set<string>();
  Object.values(modelData).forEach((levelData: any) => {
    if (levelData && Array.isArray(levelData)) {
      levelData.forEach((timeStr: string) => {
        const parts = timeStr.split('_');
        if (parts.length === 2) {
          dateHourSet.add(`${parts[0]}_Z${parts[1]}`);
        }
      });
    }
  });
  dateHourSet.forEach(key => {
    const parts = key.split('_');
    range.push({ date: parts[0], hour: parts[1], raw: key });
  });
  return range;
}

// Helper to get available times for winds
export function getAvailableWindsTimes(manifest: Manifest, model: string): TimeStep[] {
  const range: TimeStep[] = [];
  if (!manifest?.winds) return range;
  const modelData = manifest.winds.available_by_model?.[model] || manifest.winds.available?.[model] || {};
  Object.keys(modelData).forEach(date => {
    if (Array.isArray(modelData[date])) {
      modelData[date].forEach((hour: string) => {
        range.push({ date, hour: `Z${hour}`, raw: `${date}_Z${hour}` });
      });
    }
  });
  return range;
}

// Helper to get available times for smoke
export function getAvailableSmokeTimes(manifest: Manifest, model: string): TimeStep[] {
  const range: TimeStep[] = [];
  if (!manifest?.smoke) return range;
  const modelData = manifest.smoke.available_by_model?.[model] || manifest.smoke.available?.[model] || {};
  const dateHourSet = new Set<string>();
  Object.values(modelData).forEach((levelData: any) => {
    if (levelData && typeof levelData === 'object') {
      Object.keys(levelData).forEach(date => {
        if (Array.isArray(levelData[date])) {
          levelData[date].forEach((hour: string) => {
            dateHourSet.add(`${date}_Z${hour}`);
          });
        }
      });
    }
  });
  dateHourSet.forEach(key => {
    const parts = key.split('_');
    range.push({ date: parts[0], hour: parts[1], raw: key });
  });
  return range;
}

// Helper to get available times for dust
export function getAvailableDustTimes(manifest: Manifest): TimeStep[] {
  return getAvailableSmokeTimes(manifest, 'gefs_dust');
}

export function cleanHour(h: string): string {
  return h.replace('Z', '').replace('UTC', '');
}

// General function to resolve the closest timestep from a list of available timesteps
export function getClosestTimeStep(target: Date, steps: TimeStep[]): TimeStep | null {
  if (steps.length === 0) return null;
  let bestStep = steps[0];
  let minDiff = Infinity;
  steps.forEach(step => {
    let hourPart = cleanHour(step.hour);
    let stepDate: Date;
    if (hourPart === 'sunrise' || hourPart === 'alpenglow_morning' || hourPart === 'goldenhour_morning') {
      stepDate = new Date(`${step.date}T13:00:00Z`);
    } else if (hourPart === 'sunset' || hourPart === 'alpenglow_evening' || hourPart === 'goldenhour_evening') {
      const baseD = new Date(`${step.date}T00:00:00Z`);
      stepDate = new Date(baseD.getTime() + 26 * 3600 * 1000);
    } else {
      if (!hourPart.includes(':')) {
        hourPart = `${hourPart.padStart(2, '0')}:00`;
      }
      stepDate = new Date(`${step.date}T${hourPart}:00Z`);
    }
    const diff = Math.abs(stepDate.getTime() - target.getTime());
    if (diff < minDiff) {
      minDiff = diff;
      bestStep = step;
    }
  });
  return bestStep;
}

export interface TimeStep {
  date: string;
  hour: string;
  raw: string;
}

// Helper to get available times for lenticular
export function getAvailableLenticularTimes(manifest: Manifest, model: string): TimeStep[] {
  const range: TimeStep[] = [];
  if (!manifest?.lenticular) return range;
  const effectiveModel = (model === 'blend' || model === 'ensemble' || !model) ? 'ecmwf' : model;
  let modelData = manifest.lenticular.available_by_model?.[effectiveModel] || manifest.lenticular.available?.[effectiveModel];
  if (!modelData && manifest.lenticular.available_by_model) {
    const availableModels = Object.keys(manifest.lenticular.available_by_model);
    if (availableModels.length > 0) {
      modelData = manifest.lenticular.available_by_model[availableModels[0]];
    }
  }
  if (!modelData) modelData = {};
  const dateHourSet = new Set<string>();
  Object.values(modelData).forEach((modeData: any) => {
    if (modeData && typeof modeData === 'object') {
      Object.keys(modeData).forEach(date => {
        if (Array.isArray(modeData[date])) {
          modeData[date].forEach((hour: string) => {
            dateHourSet.add(`${date}_Z${hour}`);
          });
        }
      });
    }
  });
  dateHourSet.forEach(key => {
    const parts = key.split('_');
    range.push({ date: parts[0], hour: parts[1], raw: key });
  });
  return range;
}

export function getLenticularWindGridUrl(manifest: Manifest | null, model: string, mode: string, dateStr: string, hourStr: string): string {
  const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
  const effectiveModel = (model === 'blend' || model === 'ensemble' || !model) ? 'ecmwf' : model;
  const apiServer = manifest?.api_server || '';
  const path = `${tileRoot}/forecast/lenticular/lenticular_${effectiveModel}_${mode}_${dateStr}_Z${hourStr}/wind_grid.json`;
  return appendAuthToken(cleanUrl(apiServer ? `${apiServer}/${path}` : path));
}

export function getJetStreamWindGridUrl(manifest: Manifest | null, model: string, dateStr: string, hourStr: string): string {
  const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
  const effectiveModel = (model === 'blend' || !model) ? 'gfs' : model;
  const apiServer = manifest?.api_server || '';
  const cleanHour = hourStr.replace(/^Z/, '').padStart(2, '0');
  const path = `${tileRoot}/forecast/jet_stream/jet_stream_vector_${effectiveModel}_${dateStr}_Z${cleanHour}/wind_grid.json`;
  return appendAuthToken(cleanUrl(apiServer ? `${apiServer}/${path}` : path));
}

export function appendAuthToken(url: string): string {
  if (!url) return url;
  const isInternal = url.includes('auth.skyskout.com') ||
    url.startsWith('/') ||
    (typeof window !== 'undefined' && url.startsWith(window.location.origin));
  if (!isInternal) return url;
  const token = getActiveSessionToken();
  if (!token) return url;
  if (url.includes('token=')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

export async function authenticatedFetch(input: RequestInfo | string, init: RequestInit = {}): Promise<Response> {
  const rawUrl = typeof input === 'string' ? input : (input instanceof Request ? input.url : String(input));
  const urlWithToken = appendAuthToken(rawUrl);
  const token = getActiveSessionToken();

  const isInternal = rawUrl.includes('auth.skyskout.com') ||
    rawUrl.startsWith('/') ||
    (typeof window !== 'undefined' && rawUrl.startsWith(window.location.origin));

  const headers = new Headers(init.headers || {});
  if (isInternal && token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const mergedInit: RequestInit = {
    ...init,
    headers,
    credentials: isInternal ? 'include' : (init.credentials || 'same-origin')
  };

  const res = await fetch(urlWithToken, mergedInit);
  if (res.status === 401 && isInternal && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('skyskout-auth-unauthorized', { detail: { status: 401, url: rawUrl } }));
  }
  return res;
}

const pmtilesCache = new Map<string, pmtiles.PMTiles>();

export function clearPmtilesCache(archiveUrl?: string): void {
  if (archiveUrl) {
    const cleanUrl = archiveUrl.startsWith('pmtiles://') ? archiveUrl.substring('pmtiles://'.length) : archiveUrl;
    pmtilesCache.delete(cleanUrl);
  } else {
    pmtilesCache.clear();
  }
}

export function createAuthenticatedFetchSource(url: string): pmtiles.FetchSource {
  const isInternal = url.includes('auth.skyskout.com') ||
    url.startsWith('/') ||
    (typeof window !== 'undefined' && url.startsWith(window.location.origin));
  const token = getActiveSessionToken();
  const headers = new Headers();
  if (isInternal && token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return new pmtiles.FetchSource(url, headers, isInternal ? 'include' : undefined);
}

export function getOrCreatePMTiles(url: string, cacheMap: Map<string, pmtiles.PMTiles> = pmtilesCache): pmtiles.PMTiles {
  const cleanUrl = url.startsWith('pmtiles://') ? url.substring('pmtiles://'.length) : url;
  const token = getActiveSessionToken();
  let instance = cacheMap.get(cleanUrl);
  if (!instance || (instance as any)._token !== token) {
    const fetchSource = createAuthenticatedFetchSource(cleanUrl);
    instance = new pmtiles.PMTiles(fetchSource);
    (instance as any)._token = token;
    cacheMap.set(cleanUrl, instance);
  }
  return instance;
}

export async function resolvePmtilesTileUrl(tileUrl: string): Promise<string> {
  if (!tileUrl.startsWith('pmtiles://')) {
    return tileUrl;
  }
  try {
    const inner = tileUrl.substring('pmtiles://'.length);
    const targetUrl = (inner.startsWith('http://') || inner.startsWith('https://') || inner.startsWith('/')) ? inner : `https://${inner}`;
    const absoluteUrl = new URL(targetUrl, window.location.href).href;
    const match = absoluteUrl.match(/^(.*\.pmtiles)\/(\d+)\/(\d+)\/(\d+)\.([a-zA-Z0-9]+)$/);
    if (!match) {
      return '';
    }
    const pmtilesArchiveUrl = match[1];
    const pmtilesZ = parseInt(match[2], 10);
    const pmtilesX = parseInt(match[3], 10);
    const pmtilesY = parseInt(match[4], 10);
    const ext = match[5];

    let instance = getOrCreatePMTiles(pmtilesArchiveUrl, pmtilesCache);

    let response;
    try {
      response = await instance.getZxy(pmtilesZ, pmtilesX, pmtilesY);
    } catch (zxyErr) {
      // If range error, ETag mismatch, or stale header occurs, evict cached instance and retry once
      pmtilesCache.delete(pmtilesArchiveUrl);
      instance = getOrCreatePMTiles(pmtilesArchiveUrl, pmtilesCache);
      response = await instance.getZxy(pmtilesZ, pmtilesX, pmtilesY);
    }

    if (!response || !response.data || (response.data as any).byteLength === 0) {
      return '';
    }
    const mimeType = ext === 'png' ? 'image/png' : 'image/webp';
    const blob = new Blob([response.data], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (e) {
    // Missing dates, sparse tiles, or 404s gracefully resolve to empty string
    return '';
  }
}

export function getRtmaWindGridUrl(manifest: Manifest | null, timeStep?: Date | string | null): string {
  const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
  if (!manifest) return `${tileRoot}/observed/rtma/rtma_surface_wind_latest/wind_grid.json`;
  const apiServer = manifest.api_server || '';
  const rtmaInfo = manifest.live_weather?.rtma_winds;
  let targetScan = rtmaInfo?.latest_scan;

  if (timeStep && rtmaInfo?.scans && rtmaInfo.scans.length > 0) {
    let targetTimeMs: number | null = null;
    if (timeStep instanceof Date) {
      targetTimeMs = timeStep.getTime();
    } else if (typeof timeStep === 'string') {
      const parsed = Date.parse(timeStep.replace('_Z', 'T') + ':00:00Z');
      if (!isNaN(parsed)) targetTimeMs = parsed;
    }
    if (targetTimeMs !== null) {
      let closestScan = targetScan;
      let minDiff = Infinity;
      for (const scan of rtmaInfo.scans) {
        const scanIso = scan.replace('_Z', 'T') + ':00:00Z';
        const scanMs = Date.parse(scanIso);
        if (!isNaN(scanMs)) {
          const diff = Math.abs(targetTimeMs - scanMs);
          if (diff < minDiff) {
            minDiff = diff;
            closestScan = scan;
          }
        }
      }
      if (closestScan) targetScan = closestScan;
    }
  }

  const scanFolder = targetScan
    ? (targetScan.startsWith('rtma_surface_wind_') ? targetScan : `rtma_surface_wind_${targetScan}`)
    : 'rtma_surface_wind_latest';

  const path = `${tileRoot}/observed/rtma/${scanFolder}/wind_grid.json`;
  return appendAuthToken(cleanUrl(apiServer ? `${apiServer}/${path}` : path));
}

export function getSatelliteSourceForLocation(lat: number, lon: number): 'goes_composite' | 'goes_east' | 'goes_west' | 'meteosat' | 'himawari' | 'global_ir' | 'gmgsi' {
  if (lat > 75 || lat < -75) {
    return 'global_ir';
  }
  if (lon >= -180 && lon < -20) {
    return 'goes_composite';
  }
  if (lon >= -20 && lon < 60) {
    return 'meteosat';
  }
  if (lon >= 60 && lon <= 180) {
    return 'himawari';
  }
  return 'goes_composite';
}

const rawActivitiesConfig: any = (() => {
  if (typeof __ACTIVITIES_CONFIG__ !== 'undefined' && __ACTIVITIES_CONFIG__) {
    if (typeof __ACTIVITIES_CONFIG__ === 'string') {
      try {
        const parsed = JSON.parse(__ACTIVITIES_CONFIG__);
        if (parsed && Object.keys(parsed).length > 0) return parsed;
      } catch (_) { }
    } else if (typeof __ACTIVITIES_CONFIG__ === 'object' && Object.keys(__ACTIVITIES_CONFIG__).length > 0) {
      return __ACTIVITIES_CONFIG__;
    }
  }
  return defaultActivitiesJson;
})();

export const ACTIVITY_SETTINGS = rawActivitiesConfig.settings || {
  max_custom_activities_pro: 5,
  default_enabled_activities: ['standard_forecast', 'hiking', 'photography', 'stargazing', 'winter_sports', 'water_sports', 'advanced']
};

export const MASTER_LAYER_CATALOG: Record<string, LayerItemConfig> = rawActivitiesConfig.layerCatalog || {};

export async function hydrateActivitiesConfigRuntime(): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return;
  try {
    const res = await fetch('activities.generated.json', { cache: 'no-cache' });
    if (!res.ok) return;
    const data = await res.json();
    if (data && data.layerCatalog && typeof data.layerCatalog === 'object') {
      for (const [lid, ldata] of Object.entries(data.layerCatalog)) {
        if (MASTER_LAYER_CATALOG[lid]) {
          Object.assign(MASTER_LAYER_CATALOG[lid], ldata);
        } else {
          MASTER_LAYER_CATALOG[lid] = ldata as LayerItemConfig;
        }
      }
    }
    if (data && data.forecastLayouts && typeof data.forecastLayouts === 'object') {
      for (const [layoutId, layoutData] of Object.entries(data.forecastLayouts)) {
        FORECAST_LAYOUTS_CONFIG[layoutId] = layoutData;
      }
    }
    if (data && data.cannedPersonas && typeof data.cannedPersonas === 'object') {
      for (const [pid, pdata] of Object.entries(data.cannedPersonas as Record<string, any>)) {
        if (CANNED_ACTIVITIES_CONFIG[pid] && pdata && pdata.forecastLayout) {
          (CANNED_ACTIVITIES_CONFIG[pid] as any).forecastLayout = pdata.forecastLayout;
        }
      }
    }
  } catch (_) {
    // Silent fallback to build-time bundled config
  }
}

export const DEFAULT_CATEGORY_ICONS: Record<string, string> = {
  sky_solar: '☀️ ✨',
  precipitation: '🌧️ ⚡',
  clouds_wind: '☁️ 💨',
  temperature: '🌡️ 🥶',
  air_quality_fires: '🫁 🔥',
  hydrology: '🌊 ❄️',
  phenology: '🍁 🌸',
  extended_climate: '📊 🌐',
};

export const LAYER_CATEGORIES: LayerCategoryInfo[] = (rawActivitiesConfig.categories || []).map((cat: any) => ({
  ...cat,
  icon: cat.icon || DEFAULT_CATEGORY_ICONS[cat.id] || ''
}));

export const CANNED_ACTIVITIES_CONFIG: Record<string, ActivityConfig> = (() => {
  const personas = rawActivitiesConfig.cannedPersonas || {};
  const res: Record<string, ActivityConfig> = {};
  for (const key of Object.keys(personas)) {
    const p = personas[key];
    res[key] = {
      ...p,
      heroLayers: p.primaryLayers || []
    };
  }
  return res;
})();

// ACTIVITIES_CONFIG exports canned activities and serves as default registry
export const ACTIVITIES_CONFIG: Record<string, ActivityConfig> = CANNED_ACTIVITIES_CONFIG;

export const FORECAST_LAYOUTS_CONFIG: Record<string, any> = (rawActivitiesConfig as any).forecastLayouts || {};

export function resolveCustomActivity(custom: CustomActivityConfig): ActivityConfig {
  const primaryLayers: LayerItemConfig[] = (custom.primaryLayerIds || [])
    .map(id => MASTER_LAYER_CATALOG[id])
    .filter(Boolean);
  const secondaryLayers: LayerItemConfig[] = (custom.secondaryLayerIds || [])
    .map(id => MASTER_LAYER_CATALOG[id])
    .filter(Boolean);

  const defaultTheme = (custom.defaultBasemap === 'terrain-mapterhorn' || custom.defaultBasemap === 'terrain' || custom.defaultBasemap === 'recent-satellite') ? 'road' : (custom.defaultBasemap || 'road');
  const basemapCfg = custom.basemapConfig || {
    theme: defaultTheme,
    mode: 'light',
    terrain3d: false,
    shading: 'color_tint'
  };

  return {
    id: custom.id,
    title: custom.title,
    icon: custom.icon || '📍',
    description: custom.description || 'Custom User Curated Activity',
    defaultBasemap: (basemapCfg.theme === 'terrain-mapterhorn' || basemapCfg.theme === 'terrain' || basemapCfg.theme === 'recent-satellite') ? 'road' : (basemapCfg.theme || defaultTheme),
    basemapConfig: basemapCfg,
    isCustom: true,
    isDefault: false,
    heroLayers: primaryLayers,
    primaryLayers,
    secondaryLayers
  };
}

export function getAllActivities(customActivities: CustomActivityConfig[] = []): Record<string, ActivityConfig> {
  const map: Record<string, ActivityConfig> = { ...CANNED_ACTIVITIES_CONFIG };
  for (const c of customActivities) {
    map[c.id] = resolveCustomActivity(c);
  }
  return map;
}

export const PRO_WEATHER_GROUPS = LAYER_CATEGORIES.map(cat => ({
  id: cat.id,
  title: cat.title,
  icon: cat.icon,
  layers: Object.values(MASTER_LAYER_CATALOG).filter((l: any) => l.category === cat.id)
})).filter(g => g.layers.length > 0);

export const BASEMAP_STYLES_CONFIG: BaseMapStyleConfig[] = [
  { key: 'road', label: 'Skyskout - OSM', thumbnailUrl: 'basemaps/topo.jpg' },
  { key: 'usgs-topo', label: 'USGS Topo', thumbnailUrl: 'basemaps/usgs.jpg' },
  { key: 'usgs-naip', label: 'USGS Satellite', thumbnailUrl: 'basemaps/satellite.jpg' }
  // Note: 'recent-satellite' (MODIS 250m & 30m HLS Mountain Scans) is disabled for now; retained in codebase for future use.
];

export interface RecentSatelliteSliceInfo {
  offset: number;
  label: string;
  ageLabel: string;
  dateStr: string;
  formattedDate: string;
  isoDate: string;
}

export function getRecentSatelliteSliceDates(now: Date = new Date()): RecentSatelliteSliceInfo[] {
  const result: RecentSatelliteSliceInfo[] = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 0; i <= 6; i++) {
    // 5-day latency for NASA HLS BRDF Nadir reflectance
    const ageDays = 5 + i;
    const d = new Date(now.getTime() - ageDays * 86400000);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const dateStr = `${yyyy}${mm}${dd}`;
    const formattedDate = `${monthNames[d.getUTCMonth()]} ${d.getUTCDate()}, ${yyyy}`;
    const isoDate = `${yyyy}-${mm}-${dd}`;
    const label = `${monthNames[d.getUTCMonth()]} ${d.getUTCDate()}`;
    const ageLabel = `${ageDays}d ago`;
    result.push({ offset: i, label, ageLabel, dateStr, formattedDate, isoDate });
  }
  return result;
}

export function getRecentModisSliceDates(now: Date = new Date()): RecentSatelliteSliceInfo[] {
  const result: RecentSatelliteSliceInfo[] = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 0; i <= 6; i++) {
    // MODIS passes available ~2-3 hours after overpass; offset starts at yesterday (1 day ago)
    const ageDays = 1 + i;
    const d = new Date(now.getTime() - ageDays * 86400000);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const dateStr = `${yyyy}${mm}${dd}`;
    const formattedDate = `${monthNames[d.getUTCMonth()]} ${d.getUTCDate()}, ${yyyy}`;
    const isoDate = `${yyyy}-${mm}-${dd}`;
    const label = i === 0 ? 'Yesterday' : `${monthNames[d.getUTCMonth()]} ${d.getUTCDate()}`;
    const ageLabel = `${ageDays}d ago`;
    result.push({ offset: i, label, ageLabel, dateStr, formattedDate, isoDate });
  }
  return result;
}




